import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';

import { PointOfSaleModel } from '../models/PointOfSaleModel';
import {
  listPointsOfSale,
  createPointOfSale,
  updatePointOfSale,
  deletePointOfSale,
} from '../services/PointOfSaleCRUD';
import { listShops, ShopModel } from '../../shops';

enum State {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  SAVING = 'SAVING',
}

type Props = {
  id: number;
};

export default function DistributorPointsOfSale({ id }: Props) {
  const intl = useIntl();
  const [state, setState] = useState<State>(State.LOADING);
  const [pointsOfSale, setPointsOfSale] = useState<Array<PointOfSaleModel>>([]);
  const [shops, setShops] = useState<Array<ShopModel>>([]);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  // Row being reassigned; undefined when the modal is adding a new one.
  const [editingPointOfSale, setEditingPointOfSale] = useState<
    PointOfSaleModel | undefined
  >(undefined);
  const [selectedShopId, setSelectedShopId] = useState<number | null>(null);

  const shopOptions = shops.map((shop) => ({
    value: shop.id,
    label: shop.location ? `${shop.name} (${shop.location})` : shop.name,
  }));

  async function fetchPointsOfSale() {
    try {
      setState(State.LOADING);
      const { data } = await listPointsOfSale({ distributor: id });
      setPointsOfSale(data);
    } catch (error) {
      console.warn(error);
    } finally {
      setState(State.IDLE);
    }
  }

  async function fetchShops() {
    try {
      const { data } = await listShops();
      setShops(data);
    } catch (error) {
      console.warn(error);
    }
  }

  useEffect(() => {
    if (id > -1) {
      fetchPointsOfSale();
      fetchShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function onAdd() {
    setEditingPointOfSale(undefined);
    setSelectedShopId(null);
    setModalVisible(true);
  }

  function onEdit(pointOfSale: PointOfSaleModel) {
    setEditingPointOfSale(pointOfSale);
    setSelectedShopId(pointOfSale.shop);
    setModalVisible(true);
  }

  async function onSave() {
    if (!selectedShopId) return;

    try {
      setState(State.SAVING);
      if (editingPointOfSale) {
        await updatePointOfSale({
          id: editingPointOfSale.id,
          distributor: id,
          shop: selectedShopId,
        });
      } else {
        await createPointOfSale({ distributor: id, shop: selectedShopId });
      }
      setModalVisible(false);
      await fetchPointsOfSale();
      toast.success(
        intl.formatMessage({ id: 'distributorPointsOfSale.saved' })
      );
    } catch (error) {
      console.warn(error);
      // The backend rejects a point of sale already held by another
      // distributor with an explanatory message; surface it as-is.
      const detail = error?.response?.data?.shop?.[0];
      toast.error(
        detail || intl.formatMessage({ id: 'distributorPointsOfSale.error' })
      );
    } finally {
      setState(State.IDLE);
    }
  }

  async function onRemove(pointOfSale: PointOfSaleModel) {
    const confirmed = window.confirm(
      intl.formatMessage({ id: 'distributorPointsOfSale.confirmRemove' })
    );
    if (!confirmed) return;

    try {
      setState(State.SAVING);
      await deletePointOfSale(pointOfSale.id);
      await fetchPointsOfSale();
      toast.success(
        intl.formatMessage({ id: 'distributorPointsOfSale.removed' })
      );
    } catch (error) {
      console.warn(error);
      toast.error(intl.formatMessage({ id: 'distributorPointsOfSale.error' }));
    } finally {
      setState(State.IDLE);
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="mb-0">
            {intl.formatMessage({ id: 'distributorPointsOfSale.title' })}
          </h1>
          <button onClick={onAdd} className="btn btn-primary">
            <span className="fa fa-plus me-2" />
            {intl.formatMessage({ id: 'distributorPointsOfSale.add' })}
          </button>
        </div>

        {state === State.LOADING && (
          <div className="text-center">
            <div className="spinner-border text-primary" />
          </div>
        )}

        {state !== State.LOADING && !pointsOfSale.length && (
          <p>{intl.formatMessage({ id: 'distributorPointsOfSale.empty' })}</p>
        )}

        {state !== State.LOADING && pointsOfSale.length > 0 && (
          <table className="table table-striped table-bordered table-hover">
            <thead className="table-header sticky-top bg-white">
              <tr className="text-left">
                <th className="ps-2">
                  {intl.formatMessage({ id: 'shops.name' })}
                </th>
                <th>{intl.formatMessage({ id: 'shops.location' })}</th>
                <th>{intl.formatMessage({ id: 'shops.address' })}</th>
                <th>{intl.formatMessage({ id: 'shops.edit' })}</th>
                <th className="pe-2">
                  {intl.formatMessage({ id: 'shops.remove' })}
                </th>
              </tr>
            </thead>
            <tbody className="text-left">
              {pointsOfSale.map((pointOfSale) => (
                <tr key={pointOfSale.id}>
                  <td className="ps-2">{pointOfSale.shop_name}</td>
                  <td>{pointOfSale.shop_location}</td>
                  <td>{pointOfSale.shop_address}</td>
                  <td>
                    <button
                      onClick={() => onEdit(pointOfSale)}
                      className="btn btn-sm btn-tertiary fa fa-edit text-primary py-0"
                    />
                  </td>
                  <td className="pe-2">
                    <button
                      onClick={() => onRemove(pointOfSale)}
                      className="btn btn-sm btn-tertiary fa fa-trash text-danger py-0"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <Modal
          show={modalVisible}
          centered
          onHide={() => setModalVisible(false)}
        >
          <Modal.Body>
            <h2 className="mb-6">
              {editingPointOfSale
                ? intl.formatMessage({ id: 'distributorPointsOfSale.edit' })
                : intl.formatMessage({ id: 'distributorPointsOfSale.add' })}
            </h2>
            <label className="form-label" htmlFor="point-of-sale-shop">
              {intl.formatMessage({ id: 'distributorPointsOfSale.shop' })}
            </label>
            <Select
              inputId="point-of-sale-shop"
              className="react-select-container"
              classNamePrefix="react-select"
              options={shopOptions}
              value={
                shopOptions.find((option) => option.value === selectedShopId) ||
                null
              }
              onChange={(option) => setSelectedShopId(option?.value ?? null)}
              placeholder={intl.formatMessage({
                id: 'distributorPointsOfSale.selectShop',
              })}
              isClearable
            />
            <div className="d-flex gap-4 pt-8 justify-content-end">
              <button
                type="button"
                className="btn btn-tertiary"
                onClick={() => setModalVisible(false)}
              >
                {intl.formatMessage({ id: 'common.close' })}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onSave}
                disabled={!selectedShopId || state === State.SAVING}
              >
                {state === State.SAVING && (
                  <span className="spinner-border spinner-border-sm me-2" />
                )}
                {intl.formatMessage({ id: 'common.save' })}
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}
